import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowUpRight } from "lucide-react";
import { api } from "@/lib/api";
import { useSEO } from "@/lib/useSEO";
import { decideRobots, buildCanonical, buildPageMeta } from "@/lib/seoIndex";
import Breadcrumbs from "@/components/Breadcrumbs";

const SUPPORTED_LANGS = ["en", "de", "fr", "es"];

const CONFIG = {
  cause:    { endpoint: "causes",    apex: "cause",    pathPrefix: "cause",    i18nKey: "causes" },
  country:  { endpoint: "countries", apex: "country",  pathPrefix: "country",  i18nKey: "countries" },
  category: { endpoint: "categories", apex: "category", pathPrefix: "category", i18nKey: "categories" },
  city:     { endpoint: "cities",    apex: "city",     pathPrefix: "c",        i18nKey: "cities" },
};

/**
 * Index page for a hub axis — e.g. /causes lists all 10 causes, each
 * linking to its own /cause/:slug landing page. Great for both UX and as
 * an internal-link hub that helps Google discover every hub URL.
 */
export default function BrowseHubPage({ kind }) {
  const cfg = CONFIG[kind];
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  /* eslint-disable */
  useEffect(() => {
    setLoading(true);
    api.get(`/${cfg.endpoint}`)
      .then((r) => setItems(r.data || []))
      .finally(() => setLoading(false));
  }, [cfg.endpoint]);
  /* eslint-enable */

  const baseUrl = buildCanonical(`/${kind === "city" ? "cities" : kind === "cause" ? "causes" : kind === "country" ? "countries" : "categories"}`);
  const titleKey = `browse.${kind}.title`;
  const introKey = `browse.${kind}.intro`;
  const titleDefaults = {
    cause: "Browse pro bono opportunities by cause",
    country: "Browse pro bono opportunities by country",
    category: "Browse by how you want to help",
    city: "Browse pro bono opportunities by city",
  };
  const introDefaults = {
    cause: "Find European NGOs working on the causes you care about most — from climate and education to refugees and animal welfare.",
    country: "Each of the 9 European countries we cover has its own civic landscape. Pick yours to discover real local NGOs.",
    category: "Choose how to help — donate money, give time as a volunteer, share your professional skills pro bono, or invest for impact.",
    city: "All 24 European cities we currently aggregate. Browse offers near you or anywhere on the continent.",
  };

  const browseMeta = buildPageMeta("browseHub", { kind });

  useSEO({
    robots: decideRobots({ pageType: "browseHub" }),
    title: browseMeta.title,
    description: browseMeta.description,
    canonical: baseUrl,
    alternates: SUPPORTED_LANGS.map((l) => ({ hreflang: l, href: `${baseUrl}?lang=${l}` }))
      .concat([{ hreflang: "x-default", href: baseUrl }]),
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: titleDefaults[kind],
      url: baseUrl,
    },
  });

  const labelOf = (item) => {
    const name = item[cfg.apex];
    return t(`${cfg.i18nKey}.${name}`, { defaultValue: name });
  };

  return (
    <div data-testid={`browse-${kind}`}>
      <section className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-14">
        <Breadcrumbs crumbs={[{ label: titleDefaults[kind] }]} />
        <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight" data-testid="browse-h1">
          {t(titleKey, { defaultValue: titleDefaults[kind] })}
        </h1>
        <p className="mt-4 text-base md:text-lg text-[#3A463C] max-w-2xl">
          {t(introKey, { defaultValue: introDefaults[kind] })}
        </p>

        {loading ? (
          <div className="mt-10 grid sm:grid-cols-2 md:grid-cols-3 gap-4">
            {Array.from({ length: 9 }).map((_, i) => (
              <div key={i} className="h-20 bg-[#EFEAE0] rounded-2xl animate-pulse" />
            ))}
          </div>
        ) : (
          <div className="mt-10 grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="browse-grid">
            {items.map((item) => (
              <Link
                key={item.slug}
                to={`/${cfg.pathPrefix}/${item.slug}`}
                className="card-clay px-5 py-4 hover:shadow-md transition flex items-center justify-between"
                data-testid={`browse-${kind}-${item.slug}`}
              >
                <div>
                  <p className="font-bold">{labelOf(item)}</p>
                  {kind === "city" && item.country && (
                    <p className="text-xs text-[#5F6B61]">{t(`countries.${item.country}`, { defaultValue: item.country })}</p>
                  )}
                  <p className="text-xs text-[#5F6B61] mt-0.5">
                    {item.count} {item.count === 1
                      ? t("city.offer_singular", { defaultValue: "offer" })
                      : t("city.offer_plural", { defaultValue: "offers" })}
                  </p>
                </div>
                <ArrowUpRight size={16} strokeWidth={2} className="text-[#5B8C66]" />
              </Link>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
